import { useEffect, useState } from 'react';
import { MerchantUserModel } from '../../../models/merchantUser';
import axios from '../../../lib/axios';
import {
  DELETE_MERCHANT_USER,
  DELETE_MERCHANT_USER_PROFILE,
  FIND_MANY_MERCHANT_STORE,
  FIND_MANY_MERCHANT_USER,
  FIND_ROLE_AND_PERMISSION,
} from '../../../lib/endpoints/merchantUser';
import { StatusEnum } from '../../../enums/status';
import { FETCH_MORE_DATA_AMOUNT } from '../../../config-global';
import { numberFormat } from '../../../utils/nunberFormat';
import { MerChantModel } from '../../../models/merChantModel';
import { useTranslation } from 'react-i18next';
import { MerchantUserEnum } from '../../../enums/merchantUser';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

export const useJiffyStaffControllers = () => {
  const [tabValue, setTabValue] = useState<StatusEnum>(StatusEnum.ALL);
  const [tabMerchantType, setTabMerchantType] = useState<MerchantUserEnum>(
    MerchantUserEnum.MERCHANT
  );
  const [dataUser, setDataUser] = useState<MerchantUserModel[]>([]);

  const [branchData, setBranchData] = useState<MerChantModel[]>([]);
  const [branchValue, setBranchValue] = useState<string>(null!);

  const [selectedData, setSelectedData] = useState<MerchantUserModel>(null!);
  const [selectedUserId, setSelectedUserId] = useState<string>(null!);
  const [selectedProfileId, setSelectedProfileId] = useState<string>(null!);

  const [openMenu, setOpenMenu] = useState(null);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loadingMainData, setLoadingMainData] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingDeleteButton, setLoadingDeleteButton] = useState<boolean>(false);

  const assignMerchantToAdmin = useSelector((state: RootState) => state?.auth?.data?.merchants);
  const adminRole = useSelector((state: RootState) => state.auth?.data?.role?.role_type);

  const { t, i18n } = useTranslation('global');

  const handleSelectedBranch = (e: any) => {
    setBranchValue(e.target.value);
  };

  const handleOpenForm = () => {
    setOpenForm(true);
  };

  const handleOpenEditForm = () => {
    setOpenForm(true);
    const getData = dataUser.filter((data) => data.id === selectedUserId)[0];
    setSelectedData(getData);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setOpenMenu(null);
  };

  const handleOpenDeleteForm = () => {
    setOpenDelete(true);
  };

  const handleCloseDeleteForm = () => {
    setOpenDelete(false);
    setOpenMenu(null);
  };

  const handleOpenMenu = (event: any, getId: string, proId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedUserId(getId);
    setSelectedProfileId(proId);
  };

  const onCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleChangePage = (event: any, newPage: any) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (e: any) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  const filterDataByBranch = dataUser.filter((data) => data.merchantId === branchValue);

  const filterByRoleMerchant: MerchantUserModel[] = filterDataByBranch.filter(
    (item: MerchantUserModel) => {
      if (tabMerchantType === MerchantUserEnum.MERCHANT) {
        return item?.role_type === MerchantUserEnum.MERCHANT;
      } else if (tabMerchantType === MerchantUserEnum.STAFF) {
        return item?.role_type === MerchantUserEnum.STAFF;
      }
      return false;
    }
  )

  const filterDataByStatus: MerchantUserModel[] = filterByRoleMerchant.filter(
    (item: MerchantUserModel) => {
      switch (tabValue) {
        case StatusEnum.ALL:
          return true;
        case StatusEnum.ACTIVE:
          return !item?.isBan;
        case StatusEnum.INACTIVE:
          return item?.isBan;
        default:
          return false;
      }
    }
  );

  const filterData: MerchantUserModel[] = filterDataByStatus.filter(
    (user) =>
      user?.username?.toLowerCase().includes(searchValue.toLowerCase()) ||
      user?.profile?.lastname?.toLowerCase().includes(searchValue.toLowerCase()) ||
      user?.profile?.firstname?.toLowerCase().includes(searchValue.toLowerCase())
  );  

  const rows: MerchantUserModel[] = filterData.slice((currentPage - 1) * 10, currentPage * 10);

  const countPagination = Math.ceil(filterData.length / 10);

  const isSearchNotFound: boolean = !dataUser.length && !!searchValue;

  const numberOfActiveUsers = filterByRoleMerchant.filter((user) => !user.isBan).length;

  const numberOfInActiveUsers = filterByRoleMerchant.filter((user) => user.isBan).length;

  const handleDeleteUser = async () => {
    try {
      setOpenMenu(null);
      setLoadingDeleteButton(true);
      await Promise.all([
        axios.delete(DELETE_MERCHANT_USER + selectedUserId),
        axios.delete(DELETE_MERCHANT_USER_PROFILE + selectedProfileId),
      ]);
      await handleFetchData();
      setOpenDelete(false);
    } catch (err: any) {
      try {
        window.alert(err.response.data.message);
      } catch (err2) {
        window.alert('Server error please try again');
      }
    } finally {
      setLoadingDeleteButton(false);
    }
  };

  const handleFetchData = async () => {
    try {
      setLoadingData(true);
      const merchantUserResponse = await axios.get(FIND_MANY_MERCHANT_USER);
      const merchantUserData = merchantUserResponse.data?.data;
      const roleAndPermissionResponse = await axios.post(FIND_ROLE_AND_PERMISSION);
      const roleData = roleAndPermissionResponse.data;
      const updatedUserData = merchantUserData.map((user: MerchantUserModel) => {
        const userRole = roleData.find((role: any) => role.id === user.roleId);  
        return {
          ...user,
          role_type: userRole?.role_type,
        };
      });

      setDataUser(updatedUserData);
    } catch (err) {
      console.error('An error occurred while fetching data:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleFetchMainData = async () => {
    try {
      setLoadingMainData(true);
      const branchResponse = await axios.get(FIND_MANY_MERCHANT_STORE);
      if (branchResponse?.data?.length > 0) {
        const matchingMerchants = branchResponse.data.filter((merchant: any) =>
          assignMerchantToAdmin?.includes(merchant?.id)
        );

        if (adminRole === 'SUPER_ADMIN') {
          setBranchValue(branchResponse?.data[0]?.id);
          setBranchData(branchResponse?.data);
        } else if (adminRole === 'ADMIN') {
          if (matchingMerchants.length > 0) {
            setBranchValue(matchingMerchants[0].id);
            setBranchData(matchingMerchants);
          }
        }
      }
    } catch (err) {
    } finally {
      setLoadingMainData(false);
    }
  };

  useEffect(() => {
    handleFetchData();
    handleFetchMainData();
  }, []);

  return {
    openMenu,
    openForm,
    handleOpenForm,
    handleCloseForm,
    handleOpenMenu,
    onCloseMenu,
    rows,
    handleChangePage,
    handleSearch,
    loadingData,
    handleSetTabStatus: (newValue: StatusEnum) => {
      setTabValue(newValue);
      setCurrentPage(1);
    },
    tabMerchantType,
    handleSelectedMerchantType: (e: any) => {
      setTabMerchantType(e.target.value);
    },
    handleSelectMerchantRole: (newValue: MerchantUserEnum) => {
      setTabMerchantType(newValue);
    },
    loadingDeleteButton,
    currentPage,
    handleDeleteUser,
    handleCloseDeleteForm,
    handleOpenDeleteForm,
    handleOpenEditForm,
    openDelete,
    selectedData,
    handleFetchData,
    dataUser,
    countPagination,
    searchValue,
    tabValue,
    setSelectedData,
    isSearchNotFound,
    numberOfActiveUsers,
    numberOfInActiveUsers,
    handleCountIndex: (index: number) => numberFormat(index + 1 + 10 * (currentPage - 1)),
    branchData,
    handleSelectedBranch,
    branchValue,
    filterDataByBranch,
    filterByRoleMerchant,
    loadingMainData,
  };
};
